// src/components/google_ads/GoogleAdsCampaignsPage.tsx
// Track E6 — Google Ads campaigns list.
//
// Drilldown flow: row click → /m/mkt-google/campaigns/:subId → detail.
// Inline actions (push / pause / resume / delete) without leaving the list.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, AlertCircle, CheckCircle, ChevronRight, Clock, Loader2,
  Pause, PauseCircle, Play, Plus, RefreshCw, Send, Trash2, XCircle,
} from 'lucide-react';

import {
  ApiError, createCampaign, deleteCampaign, listCampaigns,
  microsToCurrency, pauseCampaign, pushCampaign, resumeCampaign,
  syncCampaigns,
  type BiddingStrategy, type Campaign, type CampaignIn, type CampaignStatus,
  type CampaignType,
} from '@/lib/api_google_ads';

type Notifier = (n: {
  type: 'success' | 'error' | 'info';
  title: string; message?: string; duration?: number;
}) => void;

interface Props { addNotification?: Notifier }

const STATUS_META: Record<CampaignStatus, { label: string; cls: string; icon: JSX.Element }> = {
  draft:    { label: 'Borrador',  cls: 'bg-white/10 text-white/80',          icon: <AlertCircle size={12} /> },
  pending:  { label: 'Pendiente', cls: 'bg-amber-500/20 text-amber-300',      icon: <Loader2 size={12} className="animate-spin" /> },
  enabled:  { label: 'Activa',    cls: 'bg-emerald-500/20 text-emerald-300',  icon: <CheckCircle size={12} /> },
  paused:   { label: 'Pausada',   cls: 'bg-yellow-500/20 text-yellow-200',    icon: <PauseCircle size={12} /> },
  removed:  { label: 'Eliminada', cls: 'bg-zinc-500/20 text-zinc-300',        icon: <XCircle size={12} /> },
  error:    { label: 'Error',     cls: 'bg-red-500/20 text-red-300',          icon: <XCircle size={12} /> },
};

export function GoogleAdsCampaignsPage({ addNotification }: Props) {
  const [rows, setRows] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<Record<number, string>>({});
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | CampaignStatus>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await listCampaigns({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 100,
      });
      setRows(out.items);
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'No se cargaron campañas',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, addNotification]);

  useEffect(() => { load(); }, [load]);

  // Poll every 10s while any campaign is pending; stops once nothing is in flight.
  useEffect(() => {
    if (!rows.some((r) => r.status === 'pending')) return;
    const t = setInterval(() => load(), 10000);
    return () => clearInterval(t);
  }, [rows, load]);

  const counts = useMemo(() => {
    const acc: Record<string, number> = { all: rows.length };
    for (const r of rows) acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, [rows]);

  const act = async (cmp: Campaign, op: 'push' | 'pause' | 'resume' | 'delete') => {
    setPending((p) => ({ ...p, [cmp.id]: op }));
    try {
      if (op === 'push')    await pushCampaign(cmp.id);
      if (op === 'pause')   await pauseCampaign(cmp.id);
      if (op === 'resume')  await resumeCampaign(cmp.id);
      if (op === 'delete')  await deleteCampaign(cmp.id);
      addNotification?.({
        type: 'success',
        title:
          op === 'push' ? 'Push encolado'
          : op === 'pause' ? 'Campaña pausada'
          : op === 'resume' ? 'Campaña activada'
          : 'Campaña eliminada',
      });
      await load();
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'No se pudo ejecutar',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setPending((p) => { const n = { ...p }; delete n[cmp.id]; return n; });
    }
  };

  const handleManualSync = async () => {
    try {
      await syncCampaigns();
      addNotification?.({ type: 'info', title: 'Sync encolado' });
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'Sync falló',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Activity className="text-emerald-400" /> Campañas
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Editas local → push a Google → Google devuelve el ID y el estado.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleManualSync} className="o-btn o-btn-ghost flex items-center gap-2">
            <RefreshCw size={16} /> Sync desde Google
          </button>
          <button onClick={load} disabled={loading} className="o-btn o-btn-ghost flex items-center gap-2">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
          <button onClick={() => setShowCreate(true)} className="o-btn o-btn-primary flex items-center gap-2">
            <Plus size={16} /> Nueva campaña
          </button>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {(['all', 'draft', 'pending', 'enabled', 'paused', 'error'] as const).map((id) => (
          <button
            key={id}
            onClick={() => setStatusFilter(id as any)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              statusFilter === id
                ? 'bg-white/10 border-white/20 text-white'
                : 'bg-transparent border-transparent text-gray-400 hover:text-gray-200 hover:bg-white/5'
            }`}
          >
            {id === 'all' ? 'Todas' : STATUS_META[id].label}
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-black/20">
              {counts[id] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={28} className="animate-spin text-emerald-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Activity size={40} className="mx-auto mb-3 opacity-50" />
          <p>Sin campañas{statusFilter !== 'all' ? ' con este filtro' : ''}.</p>
        </div>
      ) : (
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60 text-gray-400 uppercase text-xs">
              <tr>
                <th className="text-left px-4 py-2">Nombre</th>
                <th className="text-left px-4 py-2">Tipo</th>
                <th className="text-left px-4 py-2">Estado</th>
                <th className="text-left px-4 py-2">Presupuesto diario</th>
                <th className="text-left px-4 py-2">Google ID</th>
                <th className="text-left px-4 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {rows.map((cmp) => {
                const st = STATUS_META[cmp.status] || STATUS_META.draft;
                const busy = pending[cmp.id];
                return (
                  <tr key={cmp.id} className="hover:bg-white/[0.03]">
                    <td className="px-4 py-2">
                      <Link
                        to={`/m/mkt-google/campaigns/${cmp.id}`}
                        className="text-white hover:text-emerald-400 inline-flex items-center gap-1"
                      >
                        {cmp.name}
                        <ChevronRight size={14} className="opacity-60" />
                      </Link>
                      {cmp.error_message && (
                        <div className="text-xs text-red-400 truncate max-w-md">
                          {cmp.error_message}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-300">{cmp.campaign_type}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>
                        {st.icon} {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-300">
                      {microsToCurrency(cmp.daily_budget_micros)}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500 font-mono">
                      {cmp.google_campaign_id || '—'}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1">
                        {(cmp.status === 'draft' || cmp.status === 'error') && (
                          <button
                            onClick={() => act(cmp, 'push')}
                            disabled={!!busy}
                            className="o-btn o-btn-xs o-btn-primary flex items-center gap-1"
                            title="Push a Google"
                          >
                            {busy === 'push' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                            Push
                          </button>
                        )}
                        {cmp.status === 'enabled' && (
                          <button
                            onClick={() => act(cmp, 'pause')}
                            disabled={!!busy}
                            className="o-btn o-btn-xs o-btn-ghost flex items-center gap-1"
                          >
                            {busy === 'pause' ? <Loader2 size={12} className="animate-spin" /> : <Pause size={12} />}
                            Pausar
                          </button>
                        )}
                        {cmp.status === 'paused' && (
                          <button
                            onClick={() => act(cmp, 'resume')}
                            disabled={!!busy}
                            className="o-btn o-btn-xs o-btn-ghost flex items-center gap-1"
                          >
                            {busy === 'resume' ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                            Activar
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (!window.confirm(`¿Eliminar "${cmp.name}" localmente?`)) return;
                            act(cmp, 'delete');
                          }}
                          disabled={!!busy}
                          className="o-btn o-btn-xs o-btn-ghost text-red-400"
                        >
                          {busy === 'delete' ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateCampaignDrawer
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await load(); }}
          notify={addNotification}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Create drawer
// ═══════════════════════════════════════════════════════════════

function CreateCampaignDrawer({
  onClose, onCreated, notify,
}: {
  onClose: () => void;
  onCreated: () => void | Promise<void>;
  notify?: Notifier;
}) {
  const [form, setForm] = useState<CampaignIn>({
    name: '',
    campaign_type: 'SEARCH',
    google_customer_id: '',
    bidding_strategy_type: 'MANUAL_CPC',
    daily_budget_micros: 50_000_000,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createCampaign({
        ...form,
        google_customer_id: form.google_customer_id.replace(/-/g, '').trim(),
      });
      notify?.({ type: 'success', title: 'Campaña creada (draft)' });
      await onCreated();
    } catch (e: any) {
      notify?.({
        type: 'error',
        title: 'No se pudo crear',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Nueva campaña</h3>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Nombre</label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Primavera — Search"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-emerald-500 focus:outline-none"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Tipo</label>
                <select
                  value={form.campaign_type}
                  onChange={(e) => setForm((f) => ({ ...f, campaign_type: e.target.value as CampaignType }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                >
                  <option value="SEARCH">Search</option>
                  <option value="DISPLAY">Display</option>
                  <option value="SHOPPING">Shopping</option>
                  <option value="VIDEO">Video</option>
                  <option value="PERFORMANCE_MAX">Performance Max</option>
                  <option value="DEMAND_GEN">Demand Gen</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Estrategia</label>
                <select
                  value={form.bidding_strategy_type}
                  onChange={(e) => setForm((f) => ({ ...f, bidding_strategy_type: e.target.value as BiddingStrategy }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                >
                  <option value="MANUAL_CPC">Manual CPC</option>
                  <option value="MAXIMIZE_CLICKS">Maximize clicks</option>
                  <option value="TARGET_CPA">Target CPA</option>
                  <option value="TARGET_ROAS">Target ROAS</option>
                  <option value="MAXIMIZE_CONVERSIONS">Maximize conversions</option>
                  <option value="MAXIMIZE_CONVERSION_VALUE">Max conv. value</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Customer ID</label>
              <input
                value={form.google_customer_id}
                onChange={(e) => setForm((f) => ({ ...f, google_customer_id: e.target.value }))}
                placeholder="1234567890"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-emerald-500 focus:outline-none font-mono"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Presupuesto diario (micros — 1 MXN = 1.000.000)
              </label>
              <input
                type="number"
                min={0}
                value={form.daily_budget_micros || 0}
                onChange={(e) => setForm((f) => ({ ...f, daily_budget_micros: Number(e.target.value) || 0 }))}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                {microsToCurrency(form.daily_budget_micros || 0)} / día aprox.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-gray-700">
              <button type="button" onClick={onClose} className="o-btn o-btn-ghost">Cancelar</button>
              <button type="submit" disabled={saving} className="o-btn o-btn-primary flex items-center gap-2">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                Crear borrador
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default GoogleAdsCampaignsPage;
